import * as React from "react";
import { styled } from "utils";
import { DrawerContext, useDrawer } from "./drawerContext";
import { Portal, VisuallyHidden, Flex, Box } from "../../atoms";

const StyledOverlay = styled(Flex, {
  position: "fixed",
  justifyContent: "flex-end",
  height: "100vh",
  width: "100vw",
  top: 0,
  left: "100vw",
  backgroundColor: "hsla(0, 0%, 0%, 0.2)",
  transition: "opacity 0.5s",
  opacity: 0,
  variants: { isOpen: { true: { opacity: 1, left: 0 } } },
});

const StyledDrawer = styled(Box, {
  position: "absolute",
  right: "-60vw",
  margin: 0,
  transition: "right 0.5s",
  width: "60vw",
  height: "100vh",
  padding: "$4",
  paddingTop: "$10",
  backgroundColor: "$white",
  variants: { isOpen: { true: { right: 0 } } },
});

const Drawer: React.FC = (props) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const open = React.useCallback(() => setIsOpen(true), []);
  const close = React.useCallback(() => setIsOpen(false), []);

  return <DrawerContext.Provider value={{ isOpen, open, close }} {...props} />;
};

const DrawerContent: React.FC = ({ children }) => {
  const { isOpen, close } = useDrawer();

  React.useEffect(() => {
    const body = document && document.querySelector("body");
    if (body) {
      isOpen
        ? body.classList.add("--prevent-scroll")
        : body.classList.remove("--prevent-scroll");
    }
    return () => {
      body && body.classList.remove("--prevent-scroll");
    };
  }, [isOpen]);

  return (
    <Portal>
      <StyledOverlay isOpen={isOpen} onClick={close} aria-modal>
        <StyledDrawer
          isOpen={isOpen}
          onClick={(event) => event.stopPropagation()}
        >
          {children}
        </StyledDrawer>
      </StyledOverlay>
    </Portal>
  );
};

const DrawerToggleButton: React.FC = ({ children: child }) => {
  const { isOpen, open, close } = useDrawer();
  return (
    <>
      <VisuallyHidden>{isOpen ? "Close" : "Open"}</VisuallyHidden>
      {React.isValidElement(child)
        ? React.cloneElement(child, {
            isOpen,
            onToggle: isOpen ? close : open,
            "aria-hidden": true,
          })
        : null}
    </>
  );
};

export { Drawer, DrawerContent, DrawerToggleButton };
